import { z } from 'zod';
import { FootprintScope } from './forecast/types';

// Scope 1 and 2 do not have any categories like Scope 3 does so we represent
// each scope as a category using the following IDs
export const SCOPE_1_CATEGORY_ID = '1 Scope 1';
export const SCOPE_1_GHG_CATEGORY_IDS = [SCOPE_1_CATEGORY_ID] as const;

export const SCOPE_2_CATEGORY_ID = '2 Scope 2';
export const SCOPE_2_GHG_CATEGORY_IDS = [SCOPE_2_CATEGORY_ID] as const;

export const SCOPE_3_1_ID = '3.1 Purchased goods and services';
export const SCOPE_3_2_ID = '3.2 Capital goods';
export const SCOPE_3_3_ID = '3.3 Fuel and energy related activities';
export const SCOPE_3_4_ID = '3.4 Upstream transportation & distribution';
export const SCOPE_3_5_ID = '3.5 Waste generated in operations';
export const SCOPE_3_6_ID = '3.6 Business travel';
export const SCOPE_3_7_ID = '3.7 Employee commuting';
export const SCOPE_3_8_ID = '3.8 Upstream leased assets';
export const SCOPE_3_9_ID = '3.9 Downstream transportation and distribution';
export const SCOPE_3_10_ID = '3.10 Processing of sold products';
export const SCOPE_3_11_ID = '3.11 Use of sold products';
export const SCOPE_3_12_ID = '3.12 End-of-life treatment of sold products';
export const SCOPE_3_13_ID = '3.13 Downstream leased assets';
export const SCOPE_3_14_ID = '3.14 Franchises';
export const SCOPE_3_15_ID = '3.15 Investments';

export const ALL_GHG_IDS = [
  SCOPE_1_CATEGORY_ID,
  SCOPE_2_CATEGORY_ID,
  SCOPE_3_1_ID,
  SCOPE_3_2_ID,
  SCOPE_3_3_ID,
  SCOPE_3_4_ID,
  SCOPE_3_5_ID,
  SCOPE_3_6_ID,
  SCOPE_3_7_ID,
  SCOPE_3_8_ID,
  SCOPE_3_9_ID,
  SCOPE_3_10_ID,
  SCOPE_3_11_ID,
  SCOPE_3_12_ID,
  SCOPE_3_13_ID,
  SCOPE_3_14_ID,
  SCOPE_3_15_ID,
] as const;
export type GhgId = (typeof ALL_GHG_IDS)[number];

export const SCOPE_3_OTHER_UPSTREAM_ID = 'Other Upstream';
export const SCOPE_3_OTHER_DOWNSTREAM_ID = 'Other Downstream';

export const SCOPE_3_GHG_OFFICIAL_CATEGORY_IDS = [
  SCOPE_3_1_ID,
  SCOPE_3_2_ID,
  SCOPE_3_3_ID,
  SCOPE_3_4_ID,
  SCOPE_3_5_ID,
  SCOPE_3_6_ID,
  SCOPE_3_7_ID,
  SCOPE_3_8_ID,
  SCOPE_3_9_ID,
  SCOPE_3_10_ID,
  SCOPE_3_11_ID,
  SCOPE_3_12_ID,
  SCOPE_3_13_ID,
  SCOPE_3_14_ID,
  SCOPE_3_15_ID,
] as const;

// Same as above but includes two additional categories
// for misc. emissions.
export const SCOPE_3_GHG_CATEGORY_IDS = [
  ...SCOPE_3_GHG_OFFICIAL_CATEGORY_IDS,
  SCOPE_3_OTHER_UPSTREAM_ID,
  SCOPE_3_OTHER_DOWNSTREAM_ID,
] as const;
export type Scope3GhgCategoryId = (typeof SCOPE_3_GHG_CATEGORY_IDS)[number];

export const DOWNSTREAM_SCOPE_3_GHG_CATEGORY_IDS = [
  SCOPE_3_3_ID,
  SCOPE_3_9_ID,
  SCOPE_3_10_ID,
  SCOPE_3_11_ID,
  SCOPE_3_12_ID,
  SCOPE_3_13_ID,
  SCOPE_3_14_ID,
  SCOPE_3_15_ID,
  SCOPE_3_OTHER_DOWNSTREAM_ID,
] as const;

// GHG categories expected to be associated with a supplier
export const SCOPE_3_GHG_CATEGORY_IDS_WITH_SUPPLIERS = [
  SCOPE_3_1_ID,
  SCOPE_3_2_ID,
  SCOPE_3_4_ID,
  SCOPE_3_5_ID,
  SCOPE_3_6_ID,
  SCOPE_3_8_ID,
  SCOPE_3_9_ID,
  SCOPE_3_OTHER_UPSTREAM_ID,
] as const;

export const OTHER_CARBON_REMOVAL = 'other_carbon_removal';

export const CARBON_REMOVAL_CATEGORY_IDS = [OTHER_CARBON_REMOVAL] as const;

export const CLEAN_POWER_CATEGORY_IDS = [
  'other_additional_clean_power',
] as const;

export const CLEAN_POWER_ALLOCATABLE_GHG_CATEGORY_IDS_SCHEMA = z.enum([
  ...SCOPE_2_GHG_CATEGORY_IDS,
  SCOPE_3_1_ID,
  SCOPE_3_3_ID,
  SCOPE_3_7_ID,
  SCOPE_3_8_ID,
  SCOPE_3_13_ID,
]);

export const GHG_CATEGORY_DISPLAY_OVERRIDE: Partial<Record<GhgId, string>> = {
  [SCOPE_2_CATEGORY_ID]: 'Scope 2',
  [SCOPE_3_1_ID]: '3.1 Purchased goods and services (data centers only)',
};

export const OTHER_GHG_CATEGORY_IDS_RAW = [
  ...CARBON_REMOVAL_CATEGORY_IDS,
  ...CLEAN_POWER_CATEGORY_IDS,
] as const;

export type OtherGhgCategoryId = (typeof OTHER_GHG_CATEGORY_IDS_RAW)[number];

export const GHG_CATEGORY_IDS = [
  ...SCOPE_1_GHG_CATEGORY_IDS,
  ...SCOPE_2_GHG_CATEGORY_IDS,
  ...SCOPE_3_GHG_CATEGORY_IDS,
] as const;

export const SCOPE_1_2_GHG_CATEGORY_IDS = [
  ...SCOPE_1_GHG_CATEGORY_IDS,
  ...SCOPE_2_GHG_CATEGORY_IDS,
];

export type GhgCategoryId = (typeof GHG_CATEGORY_IDS)[number];

export const PIPELINE_ACCEPTED_GHG_CATEGORY_IDS = [
  ...GHG_CATEGORY_IDS,
  ...OTHER_GHG_CATEGORY_IDS_RAW,
] as const;

export type PipelineAcceptedGhgCategoryId =
  (typeof PIPELINE_ACCEPTED_GHG_CATEGORY_IDS)[number];

export const SCOPE_1 = 'Scope 1';
export const SCOPE_2 = 'Scope 2';
export const SCOPE_3 = 'Scope 3';
export const GHG_SCOPES = [SCOPE_1, SCOPE_2, SCOPE_3] as const;
export type GhgScope = (typeof GHG_SCOPES)[number];

export const GHG_SCOPE_DISPLAY_NAMES = [
  ...GHG_SCOPES,
  'Carbon removal',
  'Additional clean power purchased',
] as const;
export type GhgScopeDisplayName = (typeof GHG_SCOPE_DISPLAY_NAMES)[number];

export const SCOPE_CATEGORY_IDS: Record<
  GhgScope,
  | typeof SCOPE_1_GHG_CATEGORY_IDS
  | typeof SCOPE_2_GHG_CATEGORY_IDS
  | typeof SCOPE_3_GHG_CATEGORY_IDS
> = {
  'Scope 1': SCOPE_1_GHG_CATEGORY_IDS,
  'Scope 2': SCOPE_2_GHG_CATEGORY_IDS,
  'Scope 3': SCOPE_3_GHG_CATEGORY_IDS,
};

export const SCOPE_NAMES: Record<string, string> = {
  s1: 'Scope 1: Direct Emissions',
  s2Location: 'Scope 2: Purchased Electricity',
  s2Market: 'Scope 2: Purchased Electricity',
  s3PurchasedGoodsAndServices: 'Scope 3: Purchased Goods & Services',
  s3CapitalGoods: 'Scope 3: Capital Goods',
  s3FuelAndEnergyRelatedActivities: 'Scope 3: Fuel & Energy Activities',
  s3UpstreamTransportationAndDistribution: 'Scope 3: Upstream Transport',
  s3WasteGeneratedInOperations: 'Scope 3: Waste Generated In Operations',
  s3BusinessTravel: 'Scope 3: Business Travel',
  s3EmployeeCommuting: 'Scope 3: Employee Commuting',
  s3UpstreamLeasedAssets: 'Scope 3: Upstream Leased Assets',
  s3DownstreamTransportationAndDistribution: 'Scope 3: Downstream Transport',
  s3ProcessingOfSoldProducts: 'Scope 3: Processing Of Sold Products',
  s3UseOfSoldProducts: 'Scope 3: Use Of Sold Products',
  s3EndOfLifeTreatmentOfSoldProducts:
    'Scope 3: End Of Life Treatment Of Sold Products',
  s3DownstreamLeasedAssets: 'Scope 3: Downstream Leased Assets',
  s3Franchises: 'Scope 3: Franchises',
  s3Investments: 'Scope 3: Investments',
  s3OtherUpstream: 'Scope 3: Other Upstream',
  s3OtherDownstream: 'Scope 3: Other Downstream',
  Other: 'Other',
  OtherCarbonRemoval: 'Other: Carbon Removal',
  OtherAdditionalCleanPower: 'Other: Additional Clean Power Purchased',
};

export enum Scopes {
  s1 = 's1',
  s2Location = 's2Location',
  s2Market = 's2Market',
  s3 = 's3',
  s3PurchasedGoodsAndServices = 's3PurchasedGoodsAndServices',
  s3CapitalGoods = 's3CapitalGoods',
  s3FuelAndEnergyRelatedActivities = 's3FuelAndEnergyRelatedActivities',
  s3UpstreamTransportationAndDistribution = 's3UpstreamTransportationAndDistribution',
  s3WasteGeneratedInOperations = 's3WasteGeneratedInOperations',
  s3BusinessTravel = 's3BusinessTravel',
  s3EmployeeCommuting = 's3EmployeeCommuting',
  s3UpstreamLeasedAssets = 's3UpstreamLeasedAssets',
  s3DownstreamTransportationAndDistribution = 's3DownstreamTransportationAndDistribution',
  s3ProcessingOfSoldProducts = 's3ProcessingOfSoldProducts',
  s3UseOfSoldProducts = 's3UseOfSoldProducts',
  s3EndOfLifeTreatmentOfSoldProducts = 's3EndOfLifeTreatmentOfSoldProducts',
  s3DownstreamLeasedAssets = 's3DownstreamLeasedAssets',
  s3Franchises = 's3Franchises',
  s3Investments = 's3Investments',
  s3OtherUpstream = 's3OtherUpstream',
  s3OtherDownstream = 's3OtherDownstream',
  Other = 'Other',
  OtherCarbonRemoval = 'OtherCarbonRemoval',
  OtherAdditionalCleanPower = 'OtherAdditionalCleanPower',
}

export const SCOPE_SHORTNAMES: Record<Exclude<Scopes, 's3'>, string> = {
  s1: 'Direct Emissions',
  s2Location: 'Purchased Electricity',
  s2Market: 'Purchased Electricity',
  s3PurchasedGoodsAndServices: 'Purchased Goods & Services',
  s3CapitalGoods: 'Capital Goods',
  s3FuelAndEnergyRelatedActivities: 'Fuel & Energy Activities',
  s3UpstreamTransportationAndDistribution: 'Upstream Transport',
  s3WasteGeneratedInOperations: 'Waste Generated In Operations',
  s3BusinessTravel: 'Business Travel',
  s3EmployeeCommuting: 'Employee Commuting',
  s3UpstreamLeasedAssets: 'Upstream Leased Assets',
  s3DownstreamTransportationAndDistribution: 'Downstream Transport',
  s3ProcessingOfSoldProducts: 'Processing Of Sold Products',
  s3UseOfSoldProducts: 'Use Of Sold Products',
  s3EndOfLifeTreatmentOfSoldProducts: 'End Of Life Treatment Of Sold Products',
  s3DownstreamLeasedAssets: 'Downstream Leased Assets',
  s3Franchises: 'Franchises',
  s3Investments: 'Investments',
  s3OtherUpstream: 'Other Upstream',
  s3OtherDownstream: 'Other Downstream',
  Other: 'Other',
  OtherCarbonRemoval: 'Carbon Removal',
  OtherAdditionalCleanPower: 'Additional Clean Power Purchased',
};

export const SCOPE_DESCRIPTIONS_SHORT: Record<GhgScope, string> = {
  'Scope 1': 'Direct emissions from sources you own or control',
  'Scope 2':
    'Indirect emissions from purchased electricity, steam, heat, or cooling',
  'Scope 3': 'Indirect emissions from your business, suppliers, and customers',
};

export const SCOPE_DESCRIPTIONS_SHORT_BY_NAME: Record<FootprintScope, string> =
  {
    'scope 1': SCOPE_DESCRIPTIONS_SHORT['Scope 1'],
    'scope 2': SCOPE_DESCRIPTIONS_SHORT['Scope 2'],
    'scope 3': SCOPE_DESCRIPTIONS_SHORT['Scope 3'],
  };

export const SCOPE_DESCRIPTIONS: Record<Scopes, string> = {
  s1: 'Direct release of greenhouse gases from sources you own or control (e.g. company-owned vehicles or furnaces).',
  s2Location:
    'Emissions from the generation of electricity, steam, heat, or cooling that you purchase from companies (like your power utility).',
  s2Market:
    'Emissions from the generation of electricity, steam, heat, or cooling that you purchase from companies (like your power utility).',
  s3: 'Upstream emissions of products and services you purchase, and downstream emissions when customers use/dispose of your products.',
  s3PurchasedGoodsAndServices:
    'Emissions from the creation of products and services you purchase (e.g. materials, cloud servers, consulting services).',
  s3CapitalGoods:
    'Emissions from the production of capital goods (e.g. equipment, machinery, buildings, vehicles) you purchased.',
  s3FuelAndEnergyRelatedActivities:
    'Miscellaneous emissions connected to energy you purchase (e.g. power losses in the grid).',
  s3UpstreamTransportationAndDistribution:
    'Emissions from moving products from your supplier to you, and from you to your customers.',
  s3WasteGeneratedInOperations:
    'Emissions from the disposal of waste at your facilities (e.g. food waste from your cafeteria; discarded materials from your store).',
  s3BusinessTravel:
    'Emissions from travel on planes, trains, buses, and cars for work trips.',
  s3EmployeeCommuting:
    'Emissions from employees traveling to work (e.g. cars, carpooling, public transit).',
  s3UpstreamLeasedAssets:
    'Emissions from assets you lease that are not considered in another category.',
  s3DownstreamTransportationAndDistribution:
    'Emissions from moving products after they are sold.',
  s3ProcessingOfSoldProducts:
    'Emissions from downstream manufacturers processing your products.',
  s3UseOfSoldProducts:
    'Emissions from end customers using your product (e.g. electricity used to charge your product).',
  s3EndOfLifeTreatmentOfSoldProducts:
    'Emissions from end customers disposing of your product (e.g. recycling your paper cup).',
  s3DownstreamLeasedAssets:
    'Emissions from assets you owned that are leased by others (e.g. electricity used by your tenant to power your building).',
  s3Franchises:
    'If you grant franchises to others, this category includes emissions from your franchisees.',
  s3Investments: 'Emissions from businesses in which you have invested.',
  s3OtherUpstream: 'Other emissions emitted from your suppliers.',
  s3OtherDownstream: 'Other emissions emitted after your product is sold.',
  Other:
    'Includes several miscellaneous categories (e.g. emissions from power losses on the electricity grid, your customers’ disposal of your products).',
  OtherCarbonRemoval: 'Purchased and delivered carbon removal credits',
  OtherAdditionalCleanPower:
    'Additional clean power purchased that is not applied to specific building emissions',
};

export const GHG_ID_DESCRIPTIONS: Record<GhgId, string> = {
  [SCOPE_1_CATEGORY_ID]: SCOPE_DESCRIPTIONS['s1'],
  [SCOPE_2_CATEGORY_ID]: SCOPE_DESCRIPTIONS['s2Location'],
  [SCOPE_3_1_ID]: SCOPE_DESCRIPTIONS['s3PurchasedGoodsAndServices'],
  [SCOPE_3_2_ID]: SCOPE_DESCRIPTIONS['s3CapitalGoods'],
  [SCOPE_3_3_ID]: SCOPE_DESCRIPTIONS['s3FuelAndEnergyRelatedActivities'],
  [SCOPE_3_4_ID]: SCOPE_DESCRIPTIONS['s3UpstreamTransportationAndDistribution'],
  [SCOPE_3_5_ID]: SCOPE_DESCRIPTIONS['s3WasteGeneratedInOperations'],
  [SCOPE_3_6_ID]: SCOPE_DESCRIPTIONS['s3BusinessTravel'],
  [SCOPE_3_7_ID]: SCOPE_DESCRIPTIONS['s3EmployeeCommuting'],
  [SCOPE_3_8_ID]: SCOPE_DESCRIPTIONS['s3UpstreamLeasedAssets'],
  [SCOPE_3_9_ID]:
    SCOPE_DESCRIPTIONS['s3DownstreamTransportationAndDistribution'],
  [SCOPE_3_10_ID]: SCOPE_DESCRIPTIONS['s3ProcessingOfSoldProducts'],
  [SCOPE_3_11_ID]: SCOPE_DESCRIPTIONS['s3UseOfSoldProducts'],
  [SCOPE_3_12_ID]: SCOPE_DESCRIPTIONS['s3EndOfLifeTreatmentOfSoldProducts'],
  [SCOPE_3_13_ID]: SCOPE_DESCRIPTIONS['s3DownstreamLeasedAssets'],
  [SCOPE_3_14_ID]: SCOPE_DESCRIPTIONS['s3Franchises'],
  [SCOPE_3_15_ID]: SCOPE_DESCRIPTIONS['s3Investments'],
};

// Hand-crafted Scope 3 category methodologies and method text.
// These were crafted in:
// https://www.notion.so/watershedclimate/Project-Walters-Reporting-Q2-67a857e743e4468abd1a7dca3f7fbb7d#3aa2985b1d0542c2bfec30dd29327515
export const SCOPE_3_GHG_CATEGORY_METHODOLOGIES: Record<
  (typeof SCOPE_3_GHG_CATEGORY_IDS)[number],
  Array<string>
> = {
  [SCOPE_3_1_ID]: [
    'Supplier-specific method, spend-based method, average data method',
    `For most purchased goods and services estimates, we calculate emissions using the EPA Environmentally Extended Economic Input Output (EEIO) emissions factors applied to annual supplier and procurement spend data. Spend is aggregated by each accounting category to get total spend. Each accounting category is mapped to the most accurate EEIO category.

Spend with select vendors are mapped to those vendors’ unique revenue intensity estimates when complete and reported to the Carbon Disclosure Project (CDP).

Total spend is multiplied by the EPA EF for that category or for that vendor to calculate CO2e emissions.

To prevent double counting, supplier spend data that is accounted for under alternative scopes are removed from this analysis (e.g. electricity from facilities).

For cloud computing emissions, we use either cloud usage data or spend data to estimate electricity consumed and calculate electricity emissions by applying regional EFs. We also use spend data to estimate the indirect emissions associated with the cloud vendor.

For some physical goods where we have SKU data, BOMs are used to separate the SKU mass into individual commodities, which are multiplied by the total SKUs purchased to obtain the total mass per commodity per SKU. Mass is aggregated by each commodity to get total mass per commodity, and each commodity is mapped to the most accurate Emissions Factor(s). We multiply total mass by the Emissions Factor(s) for that commodity to calculate CO2e emissions.`,
  ],
  [SCOPE_3_2_ID]: [
    'Spend-based method, supplier-specific method',
    `We calculate emissions using the EPA Environmentally Extended Economic Input Output (EEIO) emissions factors applied to annual supplier & procurement spend data.

Spend is aggregated by each accounting category to get total spend. Each accounting category is mapped to the most accurate EEIO category. Spend with select vendors is mapped to those vendors’ unique revenue intensity estimates when they have submitted complete reports to complete and reported to the Carbon Disclosure Project (CDP).

Total spend is multiplied by the Emissions Factor for that category or for that vendor to calculate CO2e emissions. To prevent double counting, supplier spend data that is accounted for under alternative scopes are removed from this analysis.`,
  ],
  [SCOPE_3_3_ID]: [
    'Spend-based method, site-specific method, fuel-based method',
    `We estimate fuel and energy related activities emissions for two categories:

1) Transmission and Distribution - We estimate electricity lost to transmission and distribution. We apply regional grid loss rates from eGRID and Ecoinvent to estimate electricity lost in transmission and distribution, and apply the correct electricity emissions factor to estimate emissions.

2) Natural Gas Leakage - We use fugitive emissions data from chapter 4.2 of the 2019 Refinement to the 2006 IPCC Guidelines for National Greenhouse Gas inventories. A tier 1 approach was taken to evaluate fugitive emissions from exploration, production, processing, and transmission & storage of natural gas.  Tier 1 was chosen as specific supply chain data was unavailable, and fugitive natural gas emissions are typically not significant for Watershed customers.`,
  ],
  [SCOPE_3_4_ID]: [
    'Distance-based method, spend-based method',
    `We estimate emissions through two methods:

1) In cases where we only have spend, logistics expenses are aggregated by category to get total spend.Each logistics category is mapped to the most accurate EPA USEEIO category. We multiply total spend by the EF for that category. We exclude logistics categories that are accounted for separately

2) Where we have available data on delivery distance and mass, we map the delivered goods to metric tons and multiply by distance traveled to get tonnes-km. We then choose the appropriate EF based on transportation method from EPA and DEFRA and multiply by tonnes-KM to get emissions.`,
  ],
  [SCOPE_3_5_ID]: [
    'Waste-type-specific method',
    `We estimate waste emissions by evaluating the number of employees working from each office location - this is assumed to match the number of employees that are actively commuting each day (see Scope 3.7). We use the CalRecycle benchmarks as an estimate for waste produced per employee per day. We multiply waste produced for each month by emissions factors for landfill and recycling. No waste estimate is included for work from home employees. We use emissions factors from DEFRA for landfill, composting, and recycling.1)`,
  ],
  [SCOPE_3_6_ID]: [
    'Fuel-based method, distance-based method, average data method, spend-based method',
    `We estimate three emissions inputs for business travel.

(1) Flights - We calculate the distance travelled by looking at flight routes and calculating the distance between airports. We calculate total emissions using Emissions Factors from DEFRA, grouped by category of flight (e.g. long haul, medium haul, short haul). When origin, destination, and mileage data is not available, we use spend on flights applied to the relevant EEIO emissions factor.

(2) Hotels - We calculate the number of nights stayed at a hotel using the check-in and check-out dates, and apply an emissions factor based on estimated electricity and natural consumption for an upscale hotel. When this data is not available, we use spend on hotels applied to the relevant EEIO emissions factor.

(3) For all other types of business travel (e.g. Uber, Trains), we calculate emissions using the EPA Environmentally Extended Economic Input Output (EEIO) emissions factors applied to annual spend data. Spend is aggregated by each travel category to get total spend. Each accounting category is mapped to the most accurate EEIO category.`,
  ],
  [SCOPE_3_7_ID]: [
    'Distance-based method, average data method',
    `We estimate emissions in two categories.

(1) Commute. We estimate the number of employees commuting in each location by aggregating employees by location. We exclude any remote employees, and exclude any months where employees were working from home due to COVID-19. We use data published by governments to estimate average commute mix and distance for each location, and apply that to the total number of commuting employees in each location to determine miles traveled by car, public transit, walking and biking (Example sources: US Census Bureau for US states, Euro State for select EU cities). We multiply miles by the emissions factor for that commute-method category.

(2) Remote work. We estimate that the square footage occupied by a home office is 150 square feet. We use the Department of Energy’s Building Performance Database to find benchmarks for electricity consumption per square foot of residential space and natural gas per square foot of residential space. We then multiply energy usage by the corresponding region\'s electricity and natural gas emissions factors. Since the DoE\'s data set does not assume homes are being used non-stop during working hours, we adjust these estimates up to correct for this.`,
  ],
  [SCOPE_3_8_ID]: [
    'Spend-based method, average data method',
    `We estimate emissions from upstream leased assets in two ways:

1) For leased assets where we have spend data, we calculate emissions using the EPA Environmentally Extended Economic Input Output (EEIO) emissions factors applied to annual spend data. We exclude categories that are accounted for separately (i.e. buildings)

2) For some leased assets such as shared co-working spaces, we have sq-ft estimates and then generate activity based EFs for electricity and NG and calculate emissions based on assumed activity.`,
  ],
  [SCOPE_3_9_ID]: [
    'Spend-based method, distance-based method',
    `1) In cases where we only have spend, logistics expenses are aggregated by category to get total spend.Each logistics category is mapped to the most accurate EPA USEEIO category. We multiply total spend by the EF for that category. We exclude logistics categories that are accounted for separately

2) Where we have available data on delivery distance and mass, we map the delivered goods to metric tons and multiply by distance traveled to get tonnes-km. We then choose the appropriate EF based on transportation method from EPA and DEFRA and multiply by tonnes-KM to get emissions.`,
  ],
  [SCOPE_3_10_ID]: ['N/A', '[Manual input required]'],
  [SCOPE_3_11_ID]: [
    'Methodology for direct use phase emissions, please specify, Methodology for indirect use phase emissions, please specify',
    '[Manual input required]',
  ],
  [SCOPE_3_12_ID]: [
    'Waste-type-specific method',
    `We calculate emissions by collecting data on SKU sold and SKU masses. SKU masses are multiplied by the number of units sold per SKU to determine the total waste produced of each SKU. Each SKU is mapped to the most accurate waste type per the waste disposal tab of the UK government greenhouse gas reporting conversion factors database.

We multiply the total mass of waste by the Emissions Factor for that waste type to calculate CO2e emissions.`,
  ],
  [SCOPE_3_13_ID]: ['Spend-based method', '[Manual input required]'],
  [SCOPE_3_14_ID]: ['N/A', ''],
  [SCOPE_3_15_ID]: ['N/A', ''],
  [SCOPE_3_OTHER_UPSTREAM_ID]: ['N/A', ''],
  [SCOPE_3_OTHER_DOWNSTREAM_ID]: ['N/A', ''],
};

export const INDUSTRIES = [
  'Apparel',
  'Consumer Goods',
  'Cosmetics',
  'Financial Services',
  'Food',
  'Health & Wellness',
  'Hotels',
  'Logistics',
  'Online Retailer',
  'Restaurant',
  'Software as a Service',
];

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const ONE_INDEXED_MONTHS = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

export const DATE_FORMAT_MONTH_YEAR = 'MMM yyyy';
export const DATE_FORMAT_YMD = 'yyyy-MM-dd';
export const DATE_FORMAT_MDY = 'MM/dd/yyyy';
export const DATE_FORMAT_MDY_TEXT = 'MMMM d, yyyy';
export const DATE_FORMAT_MDY_TEXT_SHORT_MONTH = 'MMM d, yyyy';
export const DATE_FORMAT_QY = 'Qq yyyy';

export const MAX_CONSECUTIVE_LOGIN_FAILURES = 15;

export const syncDataCompanySouceKeyId = `cmpsrc_2kC4ytw8JUUtQkmwcg5c`;
export const syncDataCompanyId = `comp_2wU4SxrFWoatckVehwPr`;
export const syncDataCompanyNameId = `cmpname_2um5AeozRz9gJPg68Mtb`;
export const syncDataOrgId = `org_2CC4SxrFWoR41hsFXtok`;
export const syncDataUserId = `usr_2CC4Sxs47XVWMMnw5S3z`;
export const syncDataEmployeeId = `wsem_2wy4SxrFWouR1GZXcfVV`;
export const syncDataOrgAccessId = `orga_2ph4gNYoa1rhKNIw4iCKS`;
export const mtsOrgId = 'org_2tY4dMVxKxoT8b3TV1Y4';

export const FOOTPRINT_MISSING_VERSION = 'No version';
export const CORPORATE_FOOTPRINT = 'corporate_footprint';

export const DOORDASH_ORG_ID = 'org_1JV2qFjbfqcjrFi72oG4';
